import React, { useEffect, useState, useRef } from 'react';
import { Box, Typography, CircularProgress } from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import { digilockerService } from '../../services';

export function DigilockerCallback() {
  const [searchParams] = useSearchParams();
  const [digilockerStatus, setDigilockerStatus] = useState(null);
  const [stateValue, setStateValue] = useState(null);
  const closedRef = useRef(false);
  const [jsonData, setJsonData] = useState(null);

  useEffect(() => {
    if (closedRef.current) return;

    const code = searchParams.get('code');
    const state = searchParams.get('state');

    if (code && state) {
      digilockerService
        .getAccessToken(code, state, '4111598715')
        .then(tokenData => {
          const accessToken = tokenData.access_token;
          if (accessToken) {
            return digilockerService.handleCallback(accessToken, state, '4111598715');
          } else {
            throw new Error('No access token received');
          }
        })
        .then(callbackData => {
          console.log('DigiLocker Callback Data:', callbackData);
          localStorage.setItem('digilocker_state', state);
          setStateValue(state);
          setDigilockerStatus(200);
        })
        .catch(err => {
          console.error('DigiLocker error:', err);
          let errorData;
          let statusCode = 500;
          if (err.response?.data) {
            errorData = err.response.data;
            statusCode = err.response.status;
          } else if (err.message) {
            errorData = { error: err.message };
          } else {
            errorData = { error: 'DigiLocker authentication failed' };
          }
          setJsonData(errorData);
          setDigilockerStatus(statusCode);
        });
    } else {
      const errorData = { error: 'Missing code or state in URL' };
      setJsonData(errorData);
      setDigilockerStatus(400);
    }
  }, [searchParams]);

  return (
    <>
      {/* Loading screen */}
      {digilockerStatus === null && !closedRef.current && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '100vh',
            gap: 2,
            bgcolor: 'white',
          }}
        >
          <CircularProgress size={40} />
          <Typography variant="h6">
            Processing DigiLocker authentication...
          </Typography>
        </Box>
      )}

      {/* Result screen */}
      {digilockerStatus !== null && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            bgcolor: 'white',
            textAlign: 'center',
            p: 4,
          }}
        >
          {/* ✅ Success Icon */}
          {digilockerStatus === 200 ? (
            <>
              <Box sx={{ mb: 3 }}>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="120"
                  height="120"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#4caf50"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <circle cx="12" cy="12" r="10" />
                  <path d="M9 12l2 2l4-4" />
                </svg>
              </Box>
              <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 2 }}>
                DigiLocker Authorised.
              </Typography>
              <Typography variant="body1" sx={{ mb: 1 }}>
                Transaction ID for reference:
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  fontFamily: 'monospace',
                  color: '#333',
                  mb: 3,
                }}
              >
                {stateValue || 'N/A'}
              </Typography>
              <Typography variant="body2" sx={{ color: '#555' }}>
                You may close this window.
              </Typography>
            </>
          ) : (
            <>
              {/* ❌ Failure Icon */}
              <Box sx={{ mb: 3 }}>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="120"
                  height="120"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#f44336"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <circle cx="12" cy="12" r="10" />
                  <line x1="15" y1="9" x2="9" y2="15" />
                  <line x1="9" y1="9" x2="15" y2="15" />
                </svg>
              </Box>
              <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#f44336', mb: 2 }}>
                DigiLocker Failed.
              </Typography>
              <Typography variant="body1" sx={{ color: '#555', mb: 2 }}>
                {jsonData?.error || 'Something went wrong. Please try again.'}
              </Typography>
            </>
          )}
        </Box>
      )}
    </>
  );
}